/**
 * Apollo Exporter
 *
 * Utility class to build and export ApolloInput data
 * for the Unity Apollo project.
 *
 * @version 2.0
 * @author EON Dental
 */

import { stringifyOrdered } from "../../../helpers/orderedJson.ts";
import { AttachmentModel, TreatmentStep } from "../Choho/ETP";
import type { NumberingSystem } from "../Choho/ETP";
import type {
  ApolloInput,
  DrcModel,
  GingivasModel,
  Models,
  TeethModel,
  Tooth,
  FDIToothID,
} from "./UnityApollo";

// ============================================
// Helper Functions
// ============================================

/**
 * Create a DrcModel from Uint8Array data
 */
export function createDrcModel(data: Uint8Array): DrcModel {
  return { data };
}

/**
 * Convert DrcModel data to base64 string
 */
export function drcToBase64(drc: DrcModel): string {
  let binary = "";
  for (let i = 0; i < drc.data.byteLength; i++) {
    binary += String.fromCharCode(drc.data[i]);
  }
  return btoa(binary);
}

/**
 * Create DrcModel from base64 string
 */
export function drcFromBase64(base64: string): DrcModel {
  const binary = atob(base64);
  const data = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    data[i] = binary.charCodeAt(i);
  }
  return { data };
}

/**
 * Create a Tooth object
 */
export function createTooth(
  toothId: FDIToothID,
  crownData: Uint8Array,
): Tooth {
  return {
    fdi: toothId,
    crown: createDrcModel(crownData),
  };
}

// ============================================
// ApolloExporter Class
// ============================================

export class ApolloExporter {
  private version: number;
  private numberingSystem: NumberingSystem;
  private teeth: TeethModel;
  private gingiva: GingivasModel;
  private attachments: AttachmentModel[];
  private treatmentPlan: TreatmentStep[];

  constructor(version: number = 1) {
    this.version = version;
    this.numberingSystem = "FDI";
    this.teeth = {};
    this.gingiva = {};
    this.attachments = [];
    this.treatmentPlan = [];
  }

  // ----- Builder Methods -----

  /**
   * Set the version number
   */
  setVersion(version: number): this {
    this.version = version;
    return this;
  }
  /**
   * Add a treatment step
   */
  addTreatmentStep(step: TreatmentStep): this {
    this.treatmentPlan.push(step);
    return this;
  }
  /**
   * Add a tooth
   */
  addTooth(toothId: FDIToothID, crownData: Uint8Array): this {
    this.teeth[toothId] = createTooth(toothId, crownData);
    return this;
  }

  /**
   * Add a Tooth object directly
   */
  addToothObject(toothId: FDIToothID, tooth: Tooth): this {
    this.teeth[toothId] = tooth;
    return this;
  }

  /**
   * Add an attachment object directly
   */
  addAttachment(type: string, id: string, modelData: DrcModel): this {
    this.attachments.push({
      type,
      id,
      model: modelData,
    });
    return this;
  }

  /**
   * Set maxillary (upper) gingiva
   */
  setMaxillaryGingiva(data: Uint8Array): this {
    this.gingiva.maxillary = createDrcModel(data);
    return this;
  }

  /**
   * Set mandibular (lower) gingiva
   */
  setMandibularGingiva(data: Uint8Array): this {
    this.gingiva.mandibular = createDrcModel(data);
    return this;
  }

  /**
   * Remove a tooth
   */
  removeTooth(toothId: FDIToothID): this {
    delete this.teeth[toothId];
    return this;
  }

  /**
   * Clear all teeth
   */
  clearTeeth(): this {
    this.teeth = {};
    return this;
  }

  /**
   * Clear all gingiva
   */
  clearGingiva(): this {
    this.gingiva = {};
    return this;
  }
  /**
   * Clear all attachments
   */
  clearAttachments(): this {
    this.attachments = [];
    return this;
  }
  /**
   * Clear all data
   */
  clear(): this {
    this.clearTeeth();
    this.clearGingiva();
    this.clearAttachments();
    return this;
  }

  // ----- Getters -----

  /**
   * Get tooth by ID
   */
  getTooth(toothId: FDIToothID): Tooth | undefined {
    return this.teeth[toothId];
  }

  /**
   * Check if tooth exists
   */
  hasTooth(toothId: FDIToothID): boolean {
    return toothId in this.teeth;
  }

  /**
   * Get all tooth IDs
   */
  getToothIds(): FDIToothID[] {
    return Object.keys(this.teeth) as FDIToothID[];
  }

  /**
   * Get tooth count
   */
  get toothCount(): number {
    return Object.keys(this.teeth).length;
  }

  /**
   * Check if maxillary gingiva is set
   */
  hasMaxillaryGingiva(): boolean {
    return this.gingiva.maxillary !== undefined;
  }

  /**
   * Check if mandibular gingiva is set
   */
  hasMandibularGingiva(): boolean {
    return this.gingiva.mandibular !== undefined;
  }

  // ----- Build Methods -----

  /**
   * Build the Models object
   */
  buildModels(): Models {
    return {
      teeth: this.teeth,
      gingiva: this.gingiva,
      attachments: this.attachments,
    };
  }

  /**
   * Build the final ApolloInput object
   */
  build(): ApolloInput {
    return {
      version: this.version,
      numberingSystem: this.numberingSystem,
      models: this.buildModels(),
    };
  }

  // ----- Export Methods -----

  /**
   * Convert to JSON object (with base64 encoded data)
   * Matches ApolloInput structure
   */
  toJSON(): Record<string, unknown> {
    const teethJson: Record<string, unknown> = {};
    for (const [id, tooth] of Object.entries(this.teeth)) {
      console.log(tooth.crown);

      teethJson[id] = {
        fdi: tooth.fdi,
        crown: tooth.crown.data,
      };
    }

    const gingivaJson: Record<string, unknown> = {};
    if (this.gingiva.maxillary) {
      gingivaJson.maxillary = this.gingiva.maxillary.data;
    }

    if (this.gingiva.mandibular) {
      gingivaJson.mandibular = this.gingiva.mandibular.data;
    }
    // Convert attachments
    const attachmentsJson = this.attachments.map(att => ({
      type: att.type,
      id: att.id,
      model: att.model,
    }));
    console.log(this.treatmentPlan[0].teeth);

    return {
      version: this.version,
      numberingSystem: this.numberingSystem,
      models: {
        teeth: teethJson,
        gingiva: gingivaJson,
        attachments: attachmentsJson,
      },
      teethStepInfo: this.treatmentPlan[0].teeth,
    };
  }

  /**
   * Convert to JSON string
   */
  stringify(pretty: boolean = false): string {
    return stringifyOrdered(this.toJSON(), pretty ? 2 : 0);
  }

  /**
   * Download as JSON file
   */
  download(filename: string = "apollo_input"): void {
    const jsonString = this.stringify(true);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.json`;
    a.click();

    URL.revokeObjectURL(url);

    console.log(`Downloaded: ${filename}.json`);
  }

  // ----- Summary -----

  /**
   * Get summary of contents
   */
  getSummary(): {
    version: number;
    toothCount: number;
    toothIds: FDIToothID[];
    hasMaxillaryGingiva: boolean;
    hasMandibularGingiva: boolean;
  } {
    return {
      version: this.version,
      toothCount: this.toothCount,
      toothIds: this.getToothIds(),
      hasMaxillaryGingiva: this.hasMaxillaryGingiva(),
      hasMandibularGingiva: this.hasMandibularGingiva(),
    };
  }
}

// Default export
export default ApolloExporter;
